import { Box, ExpandableSection, Link, SpaceBetween, TextContent } from '@amzn/awsui-components-core';
import HelpPanel from '@amzn/awsui-components-core/help-panel';
import React, { useMemo } from 'react';
import { CustomSidePanelWidgetsEntity, RichTextContentWidget, SidePanelDetails, TableContentWidget } from 'src/components/context/AppContextModels';
import { eFinSuiteMessages } from 'src/constants/AppConstants';
import { sanitizeHtml } from 'src/utilities/SanitizeHtml';

export interface InfoPanelDetailsHelpPanelProps {
  headerName: string;
  sidePanelDetails: SidePanelDetails | undefined;
  appDetailedDescription?: string;
  displayingInForm?: boolean;
}

export const InfoPanelDetailsHelpPanel: React.FC<InfoPanelDetailsHelpPanelProps> = (props: InfoPanelDetailsHelpPanelProps) => {
  const { displayingInForm, headerName, sidePanelDetails, appDetailedDescription } = props;

  const isNoInformationResourcesAvailable = useMemo(() => {
    return !(
      appDetailedDescription ||
      sidePanelDetails?.keyContacts?.isEnabled ||
      sidePanelDetails?.officeHours?.isEnabled ||
      sidePanelDetails?.supportLinks?.isEnabled
    );
  }, [appDetailedDescription, sidePanelDetails]);

  const renderRichTextSection = (section: RichTextContentWidget | undefined, defaultTitle: string) => {
    if (!section?.isEnabled) return <></>;
    return (
      <ExpandableSection headerText={section?.title || defaultTitle} defaultExpanded={section?.isExpandedByDefault} variant="inline">
        <div
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(section?.richTextContent || '')
          }}
        />
      </ExpandableSection>
    );
  };

  const supportLinksSection = (supportLinks: CustomSidePanelWidgetsEntity | undefined, defaultTitle: string) => {
    if (!supportLinks?.isEnabled) return <></>;
    return (
      <ExpandableSection headerText={defaultTitle} defaultExpanded={supportLinks?.isExpandedByDefault} variant="inline">
        <Box margin={{ top: 'm' }}>
          <SpaceBetween size="m" direction="vertical">
            {supportLinks.tableContent?.map((tableContent: TableContentWidget | null | undefined, index: number) => {
              return (
                <Link
                  key={tableContent?.id || `support-link-${index}`}
                  variant="secondary"
                  external
                  externalIconAriaLabel="Opens in new tab"
                  ariaLabel={`Links for ${tableContent?.displayName}`}
                  target="_blank"
                  href={tableContent?.link}
                  fontSize="body-m"
                >
                  {tableContent?.displayName}
                </Link>
              );
            })}
          </SpaceBetween>
        </Box>
      </ExpandableSection>
    );
  };

  if (isNoInformationResourcesAvailable) {
    return (
      <HelpPanel header={<h2>{headerName}</h2>}>
        <div>
          <p>{eFinSuiteMessages.NO_INFORMATION_INFO_PANEL}</p>
        </div>
      </HelpPanel>
    );
  }

  return (
    <HelpPanel header={<h2>{headerName}</h2>}>
      <SpaceBetween direction="vertical" size="m">
        {displayingInForm && (
          <Box padding={{ bottom: 'm' }}>
            <TextContent>
              <p>
                <small>{`View how your info panel looks as you enter content.`} </small>
              </p>
            </TextContent>
          </Box>
        )}
        {appDetailedDescription && <Box variant="p">{appDetailedDescription}</Box>}
        {renderRichTextSection(sidePanelDetails?.keyContacts, 'Key Contacts')}
        {renderRichTextSection(sidePanelDetails?.officeHours, 'Office Hours')}
        {supportLinksSection(sidePanelDetails?.supportLinks, 'Support Links')}
      </SpaceBetween>
    </HelpPanel>
  );
};
